import { useEffect, useRef } from "react";

export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (isTouchDevice || prefersReducedMotion) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;

    let ringX = mouseX;
    let ringY = mouseY;

    let glowX = mouseX;
    let glowY = mouseY;

    let currentTarget = null;
    let isHoveringMagnetic = false;

    const magneticSelector =
      'a, button, .btn, .project-card, .about-card, .magnetic, nav a';

    const moveCursor = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%)`;
      }

      const hovered = e.target.closest(magneticSelector);

      if (hovered) {
        currentTarget = hovered;
        isHoveringMagnetic = true;
        hovered.classList.add("magnetic-active");

        if (ringRef.current) ringRef.current.classList.add("cursor-ring-hover");
        if (glowRef.current) glowRef.current.classList.add("cursor-glow-hover");
      } else {
        if (currentTarget) {
          currentTarget.classList.remove("magnetic-active");
          currentTarget.style.transform = "";
        }

        currentTarget = null;
        isHoveringMagnetic = false;

        if (ringRef.current)
          ringRef.current.classList.remove("cursor-ring-hover");
        if (glowRef.current)
          glowRef.current.classList.remove("cursor-glow-hover");
      }
    };

    const animate = () => {
      let targetX = mouseX;
      let targetY = mouseY;

      if (isHoveringMagnetic && currentTarget) {
        const rect = currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const distX = mouseX - centerX;
        const distY = mouseY - centerY;

        const maxPull = 10;

        const pullX = Math.max(-maxPull, Math.min(maxPull, -distX * 0.18));
        const pullY = Math.max(-maxPull, Math.min(maxPull, -distY * 0.18));

        targetX = mouseX + pullX;
        targetY = mouseY + pullY;

        currentTarget.style.transform = `translate(${pullX * 0.35}px, ${
          pullY * 0.35
        }px) scale(1.02)`;
      }

      ringX += (targetX - ringX) * 0.18;
      ringY += (targetY - ringY) * 0.18;

      glowX += (targetX - glowX) * 0.1;
      glowY += (targetY - glowY) * 0.1;

      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ringX}px, ${ringY}px) translate(-50%, -50%)`;
      }

      if (glowRef.current) {
        glowRef.current.style.transform = `translate(${glowX}px, ${glowY}px) translate(-50%, -50%)`;
      }

      animationFrame = requestAnimationFrame(animate);
    };

    const handleMouseDown = () => {
      ringRef.current?.classList.add("cursor-ring-click");
    };

    const handleMouseUp = () => {
      ringRef.current?.classList.remove("cursor-ring-click");
    };

    const handleMouseLeaveWindow = () => {
      dotRef.current?.classList.add("cursor-hidden");
      ringRef.current?.classList.add("cursor-hidden");
      glowRef.current?.classList.add("cursor-hidden");
    };

    const handleMouseEnterWindow = () => {
      dotRef.current?.classList.remove("cursor-hidden");
      ringRef.current?.classList.remove("cursor-hidden");
      glowRef.current?.classList.remove("cursor-hidden");
    };

    let animationFrame = requestAnimationFrame(animate);

    window.addEventListener("mousemove", moveCursor);
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mouseleave", handleMouseLeaveWindow);
    window.addEventListener("mouseenter", handleMouseEnterWindow);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("mousemove", moveCursor);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mouseleave", handleMouseLeaveWindow);
      window.removeEventListener("mouseenter", handleMouseEnterWindow);

      if (currentTarget) {
        currentTarget.classList.remove("magnetic-active");
        currentTarget.style.transform = "";
      }
    };
  }, []);

  return (
    <>
      <div ref={glowRef} className="cursor-glow" />
      <div ref={ringRef} className="cursor-ring" />
      <div ref={dotRef} className="cursor-dot" />
    </>
  );
}